import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../../shared/components/Icons';
import { formatCharge } from '../../shared/data/common';
import { useBookings } from '../../shared/booking/BookingsContext';
import { cancelTransfer, getTransferBooking, quoteTransferCancellation } from '../../api/transfers';
import { useAuth } from '../../auth/AuthContext';
import { cancellationLines } from './data';
import { splitDateTime, whenLabel } from './searchModel';

const statusText = {
  Confirmed: ['Confirmed', 'live'],
  Pending: ['Being confirmed', ''],
  Cancelled: ['Cancelled', 'cancelled'],
  Failed: ['Not confirmed', 'cancelled'],
};

/** "NotStarted" → "Not started". */
const rideText = (status) => (status ? status.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase()).replace(/ (\w)/g, (m) => m.toLowerCase()) : '');

/**
 * Live status of a cab booked through the transfers API: ride status, driver tracking and,
 * when `allowCancel`, cancelling with the operator.
 */
export default function TransferRide({ booking, allowCancel = false }) {
  const { auth } = useAuth();
  const { pathname } = useLocation();
  const { cancelBooking } = useBookings();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ loading: true, details: null, error: '' });
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [done, setDone] = useState('');
  const supplierBookingId = booking?.supplierBookingId;
  const email = booking?.contact?.email;
  const token = auth?.accessToken;

  useEffect(() => {
    if (!supplierBookingId) return undefined;
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true, error: '' }));
    getTransferBooking(supplierBookingId, email, token, controller.signal)
      .then((details) => setState({ loading: false, details, error: '' }))
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setState({
          loading: false,
          details: null,
          error: error.status === 404
            ? 'We could not find this cab booking with the operator for these details.'
            : error.message || 'Unable to load the latest ride details.',
        });
      });
    return () => controller.abort();
  }, [supplierBookingId, email, token, attempt]);

  // Keep My Trips in step with the operator: cancelled by support, or never confirmed (and refunded).
  const liveStatus = state.details?.status;
  useEffect(() => {
    if (!booking || booking.status === 'cancelled') return;
    if (liveStatus === 'Cancelled') cancelBooking(booking.id, booking.refund || { amount: 0, note: 'Cancelled with the cab operator.' });
    if (liveStatus === 'Failed') cancelBooking(booking.id, { amount: booking.fare.total, note: 'The cab operator could not confirm this booking, so the full amount is refunded.' });
  }, [liveStatus, booking, cancelBooking]);

  if (!supplierBookingId) return null;

  const heading = <h3><Icon name="transfers" size={18} /> Your ride</h3>;
  if (state.loading && !state.details) {
    return <section className="panel ins-policies" role="status">{heading}<p className="muted">Fetching the latest from the cab operator…</p></section>;
  }
  if (state.error) {
    return (
      <section className="panel ins-policies">
        {heading}
        <p className="text-coral" role="alert">{state.error}</p>
        <button type="button" className="btn btn-outline" onClick={() => setAttempt(attempt + 1)}>Try again</button>
      </section>
    );
  }

  const { details } = state;
  const [label, pillClass] = statusText[details.status] || [details.status, ''];
  const pickup = details.pickupDateTime && splitDateTime(details.pickupDateTime);
  const canCancel = allowCancel && details.canCancel;
  const live = ['Confirmed', 'Pending'].includes(details.status);

  const getQuote = async () => {
    setBusy(true);
    setActionError('');
    setDone('');
    try {
      setQuote(await quoteTransferCancellation(supplierBookingId, token));
    } catch (error) {
      setActionError(error.message || 'We could not get a cancellation quote.');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setActionError('');
    try {
      const result = await cancelTransfer(supplierBookingId, quote.cancellationId, token);
      cancelBooking(booking.id, { amount: result.customerRefund, note: 'Convenience fee is non-refundable.' });
      setDone(`Cancelled. ${formatCharge(result.customerRefund)} will be refunded to your original payment method within 5–7 working days.`);
      setQuote(null);
      setAttempt((n) => n + 1);
    } catch (error) {
      setActionError(error.status === 409
        ? error.message
        : `${error.message || 'The cab operator did not confirm the cancellation.'} Your cab is still booked — please contact support if you need to cancel.`);
      setQuote(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel ins-policies">
      <div className="fbs-head">
        {heading}
        <span className={`status-pill ${pillClass}`}>{label}</span>
      </div>
      <div className="fbs-ids">
        <div><span className="muted small">{booking.supplierCode || details.supplierCode} booking ID</span><b>{details.supplierBookingId}</b></div>
        {pickup && <div><span className="muted small">Pickup</span><b>{whenLabel(pickup.date, pickup.time)}</b></div>}
        {live && details.rideStatus && <div><span className="muted small">Ride</span><b>{rideText(details.rideStatus)}</b></div>}
        <div><span className="muted small">Amount charged</span><b>{formatCharge(details.totalAmount)}</b></div>
      </div>

      {details.status === 'Failed' && (
        <div className="api-message error" role="alert">The cab operator could not confirm this booking. The full amount will be refunded to your original payment method.</div>
      )}

      {live && (
        <div className="kv-grid ride-grid">
          {details.vehicle && <div><span className="muted small">Cab</span><b>{details.vehicle.label}</b></div>}
          {details.passengerName && <div><span className="muted small">Passenger</span><b>{details.passengerName}</b></div>}
          {details.flightNumber && <div><span className="muted small">Flight</span><b>{details.flightNumber}</b></div>}
          {details.specialRequest && <div><span className="muted small">Note for driver</span><b>{details.specialRequest}</b></div>}
        </div>
      )}

      {live && (
        <div className="btn-row ride-actions">
          {details.trackingLink && (
            <a className="btn btn-primary" href={details.trackingLink} target="_blank" rel="noopener noreferrer">
              <Icon name="pin" size={16} /> Track your driver
            </a>
          )}
          <button type="button" className="btn btn-outline" onClick={() => setAttempt(attempt + 1)} disabled={state.loading}>
            {state.loading ? 'Refreshing…' : 'Refresh status'}
          </button>
        </div>
      )}
      {live && details.trackingLink && <p className="muted small">Tracking works once a driver is assigned, usually a few hours before pickup.</p>}
      {details.helpline && <p className="muted small"><Icon name="headset" size={13} /> {details.helpline.trim()}</p>}

      {done && <div className="api-message success" role="status">{done}</div>}
      {actionError && <div className="api-message error" role="alert">{actionError}</div>}

      {allowCancel && live && (
        <div className="ins-cancel">
          {details.cancellationRules.length > 0 && (
            <ul className="policy-list small-list">{cancellationLines(details.cancellationRules).map((l) => <li key={l}>{l}</li>)}</ul>
          )}
          {!canCancel ? (
            <p className="muted small">This cab can no longer be cancelled online. Contact support for help.</p>
          ) : !token ? (
            <p className="muted small">
              <Link className="link" to={`/account/login?returnTo=${encodeURIComponent(pathname)}`}>Sign in</Link> with {email} to cancel this cab online.
            </p>
          ) : (
            <button type="button" className="btn btn-danger" disabled={busy} onClick={getQuote}>
              {busy && !quote ? 'Checking refund…' : 'Cancel cab'}
            </button>
          )}
        </div>
      )}

      {quote && (
        <div className="overlay" onClick={() => !busy && setQuote(null)}>
          <div className="overlay-card modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Cancel cab">
            <h3>Cancel this cab?</h3>
            {quote.description && <p className="muted">{quote.description}</p>}
            <div className="refund-box">
              <div className="summary-line"><span>Amount paid</span><span>{formatCharge(details.totalAmount)}</span></div>
              <div className="summary-line total"><span>Refund</span><b className="text-primary">{formatCharge(quote.customerRefund)}</b></div>
              <p className="muted small">Credited to your original payment method within 5–7 working days. The convenience fee is not refunded. This cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-outline" disabled={busy} onClick={() => setQuote(null)}>Keep cab</button>
              <button type="button" className="btn btn-danger" disabled={busy} onClick={confirm}>{busy ? 'Cancelling…' : 'Yes, cancel'}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
